import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/env';

const JITSI_DOMAIN = 'meet.jit.si';
const JITSI_SCRIPT_SRC = `https://${JITSI_DOMAIN}/external_api.js`;

function loadJitsiScript() {
  return new Promise((resolve, reject) => {
    if (window.JitsiMeetExternalAPI) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[src="${JITSI_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('No se pudo cargar Jitsi')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = JITSI_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('No se pudo cargar Jitsi'));
    document.body.appendChild(script);
  });
}

export default function Llamada() {
  const navigate = useNavigate();
  const { callId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const inviteMode = Boolean(token);
  const [authReady, setAuthReady] = useState(false);
  const [error, setError] = useState('');
  const [ending, setEnding] = useState(false);
  const [callData, setCallData] = useState(null);
  const jitsiContainerRef = useRef(null);
  const jitsiApiRef = useRef(null);

  const roomName = useMemo(() => {
    const safeId = String(callId || '').trim();
    return safeId ? `pulseone-${safeId}` : '';
  }, [callId]);

  async function loadStatus() {
    if (!callId) return;
    try {
      const res = inviteMode
        ? await fetch(`${API_BASE_URL}/api/auth/call-invite-status`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ callId, token })
        })
        : await fetch(`${API_BASE_URL}/api/chatwoot/calls/${callId}`, {
          credentials: 'include'
        });
      if (!res.ok) return;
      const json = await res.json().catch(() => ({}));
      setCallData(json?.data || null);
    } catch {
      // silencioso
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function bootstrapAuth() {
      try {
        if (inviteMode && callId) {
          const res = await fetch(`${API_BASE_URL}/api/auth/call-invite-login`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ callId, token })
          });
          if (!res.ok) throw new Error('Invitación inválida o expirada');
        }
        if (!cancelled) {
          setError('');
          setAuthReady(true);
        }
      } catch {
        if (!cancelled) {
          setAuthReady(false);
          setError('No se pudo abrir la llamada. Verificá que el enlace sea válido.');
        }
      }
    }
    bootstrapAuth();
    return () => { cancelled = true; };
  }, [inviteMode, callId, token]);

  useEffect(() => {
    if (!authReady || !roomName || !jitsiContainerRef.current) return;
    let cancelled = false;
    async function mountJitsi() {
      try {
        await loadJitsiScript();
        if (cancelled || !jitsiContainerRef.current || !window.JitsiMeetExternalAPI) return;

        const displayName = inviteMode ? 'Invitado' : 'Agente';
        const api = new window.JitsiMeetExternalAPI(JITSI_DOMAIN, {
          roomName,
          parentNode: jitsiContainerRef.current,
          width: '100%',
          height: '100%',
          userInfo: { displayName },
          configOverwrite: {
            prejoinPageEnabled: false,
            disableDeepLinking: true,
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            disableModeratorIndicator: true,
            hideConferenceSubject: true
          },
          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            SHOW_BRAND_WATERMARK: false,
            BRAND_WATERMARK_LINK: '',
            DISPLAY_WELCOME_PAGE_CONTENT: false,
            DISPLAY_WELCOME_PAGE_TOOLBAR_ADDITIONAL_CONTENT: false,
            MOBILE_APP_PROMO: false,
            HIDE_INVITE_MORE_HEADER: true,
            DISABLE_JOIN_LEAVE_NOTIFICATIONS: true
          }
        });

        jitsiApiRef.current = api;
        api.addListener('readyToClose', () => {
          navigate('/conversaciones');
        });
      } catch {
        if (!cancelled) setError('No se pudo inicializar la videollamada.');
      }
    }
    mountJitsi();

    return () => {
      cancelled = true;
      if (jitsiApiRef.current) {
        try { jitsiApiRef.current.dispose(); } catch {}
        jitsiApiRef.current = null;
      }
    };
  }, [authReady, roomName, inviteMode, navigate]);

  useEffect(() => {
    loadStatus();
  }, [callId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBack = async () => {
    if (ending) return;
    try {
      setEnding(true);
      if (callId) {
        await fetch(`${API_BASE_URL}/api/chatwoot/calls/${callId}/end`, {
          method: 'POST',
          credentials: 'include'
        }).catch(() => {});
      }
      if (jitsiApiRef.current) {
        try { jitsiApiRef.current.executeCommand('hangup'); } catch {}
      }
      navigate('/conversaciones');
    } finally {
      setEnding(false);
    }
  };

  return (
    <section style={{ minHeight: 'calc(100vh - 64px)', background: '#0b1220', padding: 14 }}>
      <div style={{ position: 'relative', borderRadius: 18, overflow: 'hidden', border: '1px solid rgba(148,163,184,.26)', minHeight: 'calc(100vh - 92px)', background: '#0f172a', boxShadow: '0 28px 60px rgba(2,6,23,.45)' }}>
        <div ref={jitsiContainerRef} style={{ width: '100%', height: 'calc(100vh - 92px)' }} />

        <div style={{ position: 'absolute', top: 14, left: 14, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 999, background: 'rgba(15,23,42,.72)', border: '1px solid rgba(148,163,184,.35)', color: '#e2e8f0', fontWeight: 700, fontSize: 12, zIndex: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
          Videollamada activa
          <span style={{ opacity: .7 }}>•</span>
          Sala: {roomName || '-'}
        </div>

        <div style={{ position: 'absolute', top: 14, right: 14, zIndex: 2 }}>
          <button
            type="button"
            onClick={handleBack}
            disabled={ending}
            style={{ border: '1px solid rgba(248,113,113,.7)', background: 'rgba(185,28,28,.92)', color: '#fff', borderRadius: 999, padding: '10px 16px', fontWeight: 900, cursor: ending ? 'not-allowed' : 'pointer', opacity: ending ? .6 : 1 }}
          >
            {ending ? 'Finalizando...' : 'Colgar y volver'}
          </button>
        </div>

        {error ? (
          <div style={{ position: 'absolute', left: '50%', top: 58, transform: 'translateX(-50%)', border: '1px solid rgba(239,68,68,.45)', background: 'rgba(127,29,29,.72)', color: '#fecaca', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, zIndex: 2 }}>
            {error}
          </div>
        ) : null}

        {String(callData?.status || '').toLowerCase() === 'expired' ? (
          <div style={{ position: 'absolute', left: '50%', bottom: 18, transform: 'translateX(-50%)', border: '1px solid rgba(245,158,11,.45)', background: 'rgba(120,53,15,.72)', color: '#fde68a', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, zIndex: 2 }}>
            Esta llamada expiró. Generá una nueva desde Conversaciones.
          </div>
        ) : null}
      </div>
    </section>
  );
}
